import { type ModuleApiInfo } from "@client/content/selectors";
import { parseQueryString } from "@client/lib/api";
import { download, request, type RequestArgs } from "@client/lib/request";
import type { ApiResponse, HttpApiResponse, HttpResponse } from "@client/types/Api";
import type { CacheType } from "@common/lib/api";
import i18n from "@common/lib/i18n";
import { combineUrl } from "@common/lib/url";

const getArgs = (apiInfo: ModuleApiInfo, args: RequestArgs): RequestArgs => {
  const query = {
    ...apiInfo.baseParams,
    ...parseQueryString(args.url)
  };
  const url = args.url.includes("?") ? args.url.substr(0, args.url.indexOf("?")) : args.url;

  return {
    method: args.method,
    url: combineUrl(apiInfo.baseUrl, url),
    body: args.body,
    headers: args.headers,
    query: {
      ...query,
      ...(args.query || {})
    },
    i18n: apiInfo.i18n,
    cache: args.cache
  };
};

export const makeRequest = async <T extends object>(
  apiInfo: ModuleApiInfo,
  args: RequestArgs
): Promise<HttpResponse<T>> => {
  const requestArgs = getArgs(apiInfo, args);
  return request<T>(requestArgs);
};

const traverse = (obj: object, enter: (obj: object, key: string) => void, omit?: (name: string) => boolean) => {
  Object.keys(obj).forEach((k) => {
    if (omit && omit(k)) return;

    if (obj[k] && typeof obj[k] === "object") {
      traverse(obj[k], enter, omit);
    } else {
      enter(obj, k);
    }
  });
};

const localizeLabel = (label: string | null | undefined) => {
  if (!label) return "";

  return i18n[label];
};

export const localize = (data: object | Array<any>, omit?: (name: string) => boolean): any => {
  traverse(
    data,
    (obj, k) => {
      if (k.toLocaleLowerCase().endsWith("label") && obj[k]) {
        obj[k] = localizeLabel(obj[k]);
      }
    },
    omit
  );
  return data;
};

const apiRequest = async <T extends any>(apiInfo: ModuleApiInfo, args: RequestArgs): Promise<HttpApiResponse<T>> => {
  const res = await makeRequest<ApiResponse<T>>(apiInfo, args);
  if (res?.response?.data) {
    localize(res.response?.data);
  }
  return res;
};

export default {
  delete: <T extends any>(apiInfo: ModuleApiInfo, path: string, qs?: object): Promise<HttpApiResponse<T>> =>
    apiRequest(apiInfo, { method: "DELETE", url: path, query: qs }),
  download: async (apiInfo: ModuleApiInfo, path: string, qs?: object): Promise<void> => {
    const requestArgs = getArgs(apiInfo, { method: "GET", url: path, query: qs });
    await download(requestArgs);
  },
  get: <T extends any>(
    apiInfo: ModuleApiInfo,
    path: string,
    qs?: object,
    cache?: CacheType
  ): Promise<HttpApiResponse<T>> => apiRequest(apiInfo, { method: "GET", url: path, query: qs, cache }),
  post: <T extends any>(apiInfo: ModuleApiInfo, path: string, data?: object): Promise<HttpApiResponse<T>> =>
    apiRequest(apiInfo, { method: "POST", url: path, body: data }),
  put: <T extends any>(apiInfo: ModuleApiInfo, path: string, data?: object): Promise<HttpApiResponse<T>> =>
    apiRequest(apiInfo, { method: "PUT", url: path, body: data })
};

export const toHttpApiResponse = <T>(data: T): HttpApiResponse<T> => ({
  status: 200,
  response: {
    data,
    status: "success"
  }
});
